import { useState, useCallback } from 'react';
import { gcAccessToken, gcEnvironment, fetchWrap, getClientOrg } from '../../components/common/utilities/fetchWrap';
import { reportConsole } from "../../components/common/utilities/logging-utilities";
import { checkUsageTrialForTenant } from './integrationService'
import config from '../../config';

export interface KnowledgeBaseItem {
  knowledgeBaseId: string;
  knowledgeBaseName: string;
  knowledgeBaseDesc: string;
  knowledgeSourceList: KnowledgeSource[];
  contentSearch: boolean;
  locale: string;
  totalArticlesCount: number;
  lastSync: string;
  status: string;
}

export interface KnowledgeSource {
  sitePath: string;
  connectionName: string;
  connectionId: string;
  articlePaths: string[];
}

export interface NewKnowledgeBaseForm {
  id?: string;
  name: string;
  description?: string;
  locale: string;
  knowledgeSourceList: KnowledgeSource[];
  contentSearch: boolean;
  showSharePointBrowser: boolean;
  selectedContentBySite: Record<string, string[]>;
  lastSync?: string;
  totalArticlesCount?: number;
}

export interface SharePointContentItem {
  id: string;
  name: string;
  type: string;
  path: string;
  customPath: string;
  parentReference: any;
  spTenantId: string;
  sitePath: string;
  connectionName: string;
  connectionId: string;
  webUrl?: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  driveId?: string;
  driveName?: string;
  size?: number;
}

export interface FormValidationErrors {
  name?: string;
  description?: string;
  content?: string;
}

export interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  knowledgeBaseName: string;
  isDeleting: boolean;
}

export interface PreviewChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  connections: any;
  addedContent: Record<string, string[]>;
  removedContent: Record<string, string[]>;
  unchangedContent: Record<string, string[]>;
}

export interface ContentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sitePath: string;
  connectionName: string;
  connectionId: string;
  contentPaths: string[];
}

export interface SelectedContentSummaryProps {
  selectedContentBySite: Record<string, string[]>;
  onRemoveSite: (compositeKey: string) => void;
  isEditing?: boolean;
  connections: any;
  originalContentBySite?: Record<string, string[]>;
}

export interface SharePointBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
  connections: SharePointConnection[];
  selectedContentBySite: Record<string, string[]>;
  onContentSelect: (compositeKey: string, contentPath: string) => void;
}

export interface SharePointConnection {
  connectionId: string;
  connectionName: string;
  siteName: string;
  fileInclusions?: string[];
  spTenantId: string;
}

// Constants
export const API_BASE_URL = config.endpoints.endpoint || '';
export const DEFAULT_LOCALE = 'en-US';
export const LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-IN', label: 'English (India)' },
  { value: 'en-IE', label: 'English (Ireland)' },
  { value: 'en-PH', label: 'English (Philippines)' },
  { value: 'en-SG', label: 'English (Singapore)' },
  { value: 'en-ZA', label: 'English (South Africa)' },
  { value: 'en-UK', label: 'English (UK)' },
  { value: 'th-TH', label: 'Thai (001)' },
  { value: 'tr-TR', label: 'Turkish (TR)' },
  { value: 'fi-FI', label: 'Finnish (FI)' },
  { value: 'fr-BE', label: 'French (BE)' },
  { value: 'fr-CA', label: 'French (CA)' },
  { value: 'fr-CH', label: 'French (CH)' },
  { value: 'fr-FR', label: 'French (FR)' },
  { value: 'es-AR', label: 'Spanish (AR)' },
  { value: 'es-CO', label: 'Spanish (CO)' },
  { value: 'es-MX', label: 'Spanish (MX)' },
  { value: 'es-US', label: 'Spanish (US)' },
  { value: 'es-ES', label: 'Spanish (ES)' },
  { value: 'de-AT', label: 'German (AT)' },
  { value: 'de-CH', label: 'German (CH)' },
  { value: 'de-DE', label: 'German (DE)' },
  { value: 'hu-HU', label: 'Hungarian (HU)' },
  { value: 'it-IT', label: 'Italian (IT)' },
  { value: 'ja-JP', label: 'Japanese (JP)' },
  { value: 'ko-KR', label: 'Korean (KR)' },
  { value: 'sv-SE', label: 'Swedish (SE)' },
  { value: 'pt-BR', label: 'Portuguese (BR)' },
  { value: 'pt-PT', label: 'Portuguese (PT)' },
  { value: 'nb-NO', label: 'Norwegian (NO)' },
  { value: 'uk-UA', label: 'Ukrainian (UA)' },
];

// Custom Hooks
export const useApiRequest = () => {
  const [error, setError] = useState<string | null>(null);

  const executeRequest = useCallback(async <T>(
    requestFn: () => Promise<T>,
    onSuccess?: (data: T) => void,
    onError?: (err: Error) => void
  ): Promise<T | null> => {
    setError(null);
    try {
      const result = await requestFn();
      if (onSuccess) onSuccess(result);
      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'An error occurred';
      setError(errorMessage);
      if (onError) onError(err);
      reportConsole(err, "error");
      return null;
    }
  }, []);

  return { error, executeRequest };
};

export const useKnowledgeBaseApi = () => {
  const getRequestHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    env: gcEnvironment(),
    token: gcAccessToken(),
    tokensource: 'purecloud'
  }), []);

  const fetchConnections = useCallback(async (): Promise<SharePointConnection[]> => {
    try {
      const response = await fetchWrap({
        url: `${API_BASE_URL}/getSPConfiguration`,
        options: {
          method: 'POST',
          body: JSON.stringify({ tenantId: getClientOrg(), system: 'source' })
        },
        token: gcAccessToken(),
        auth: true,
        cookies: false,
        headerOptions: getRequestHeaders(),
        noContentType: false,
        contentType: 'application/json',
        noResponseData: false
      });
      return response.data.results || [];
    } catch (err: any) {
      reportConsole('Failed to fetch connections', 'error', { err });
      throw new Error(err.message || 'Failed to fetch connections');
    }
  }, [getRequestHeaders]);

  const fetchKnowledgeBases = useCallback(async (): Promise<KnowledgeBaseItem[]> => {
    try {
      const response = await fetchWrap({
        url: `${API_BASE_URL}/getSPConfiguration`,
        options: {
          method: 'POST',
          body: JSON.stringify({ tenantId: getClientOrg(), system: 'target' })
        },
        token: gcAccessToken(),
        auth: true,
        cookies: false,
        headerOptions: getRequestHeaders(),
        noContentType: false,
        contentType: 'application/json',
        noResponseData: false
      });

      const results = response.data.results || [];
      const connections = await fetchConnections();
      return results.map((item: any) => ({
        ...item,
        knowledgeSourceList: item.knowledgeSourceList.map((source: any) => {
          const connection = connections.find(
            (conn: SharePointConnection) => conn.connectionId === source.connectionId
          );
          return {
            ...source,
            connectionName: connection ? connection.connectionName : source.connectionName || 'Unknown Connection',
          };
        }),
      }));
    } catch (err: any) {
      reportConsole('Failed to fetch knowledge bases', 'error', { err });
      throw new Error(err.message || 'Failed to fetch knowledge bases');
    }
  }, [getRequestHeaders, fetchConnections]);

  const fetchSharePointContent = useCallback(async (
    siteUrl: string,
    path: string,
    fileInclusions: string[],
    spTenantId: string,
    continuationToken?: string
  ): Promise<{ items: SharePointContentItem[], pagination: { hasMore: boolean, totalItems: number, nextUrl: string | null } }> => {
    try {
      const requestBody = {
        tenantId: getClientOrg(),
        spTenantId,
        siteUrl,
        subFolderPath: path || '/',
        fileInclusions: fileInclusions || [],
        ...(continuationToken && { continuationToken })
      };

      const response = await fetchWrap({
        url: `${API_BASE_URL}/getSPContentForSite`,
        options: {
          method: 'POST',
          body: JSON.stringify(requestBody)
        },
        token: gcAccessToken(),
        auth: true,
        cookies: false,
        headerOptions: getRequestHeaders(),
        noContentType: false,
        contentType: 'application/json',
        noResponseData: false
      });

      if (!response.data ) {
        throw new Error(response.data?.message || 'Failed to fetch SharePoint content');
      }

      // SharePoint exposes a system-managed "Preservation Hold Library" that should
      // never be surfaced to end-users for selection or browsing.
      const PRESERVATION_HOLD_LIBRARY = 'preservation hold library';
      const isPreservationHoldItem = (rawItem: any): boolean => {
        const name = (rawItem?.name || '').trim().toLowerCase();
        if (name === PRESERVATION_HOLD_LIBRARY) return true;
        const customPath = (rawItem?.customPath || '').toLowerCase();
        return customPath.split('/').some((segment: string) => segment.trim() === PRESERVATION_HOLD_LIBRARY);
      };

      const filteredResults = (response.data.results || []).filter(
        (item: any) => !isPreservationHoldItem(item)
      );

      const items = filteredResults.map((item: any) => ({
        id: item.id,
        name: item.name,
        type: item.type,
        path: item.customPath,
        customPath: item.customPath,
        parentReference: item.parentReference,
        sitePath: siteUrl,
        connectionName: item.driveName || '',
        connectionId: '',
        webUrl: item.webUrl,
        createdDateTime: item.createdDateTime,
        lastModifiedDateTime: item.lastModifiedDateTime,
        driveId: item.parentReference?.driveId,
        driveName: item.driveName,
        spTenantId,
        size: item.size
      }));

      const rawTotalItems = response.data.pagination?.totalItems || 0;
      const filteredOutCount = (response.data.results || []).length - filteredResults.length;
      const adjustedTotalItems = Math.max(0, rawTotalItems - filteredOutCount);

      return {
        items,
        pagination: {
          hasMore: response.data.pagination?.hasMore || false,
          totalItems: adjustedTotalItems,
          nextUrl: response.data.pagination?.nextUrl || null
        }
      };
    } catch (err: any) {
      reportConsole('Failed to fetch SharePoint content', 'error', { err });
      throw new Error(err.message || 'Failed to fetch SharePoint content');
    }
  }, [getRequestHeaders]);

  const createOrUpdateKnowledgeBase = useCallback(async (knowledgeBaseData: any, isEditing: boolean): Promise<any> => {
    try {
      const response = await fetchWrap({
        url: `${API_BASE_URL}/setKnowledgeBase`,
        options: {
          method: 'POST',
          body: JSON.stringify({
            tenantId: getClientOrg(),
            nestedData: {
              ...knowledgeBaseData,
              knowledgeSourceList: knowledgeBaseData.knowledgeSourceList.map((source: KnowledgeSource) => ({
                sitePath: source.sitePath,
                connectionId: source.connectionId,
                articlePaths: source.articlePaths,
              })),
            },
            action: isEditing ? 'UpdateKB' : 'SaveKB'
          })
        },
        token: gcAccessToken(),
        auth: true,
        cookies: false,
        headerOptions: getRequestHeaders(),
        noContentType: false,
        contentType: 'application/json',
        noResponseData: false
      });

      if (!response.data || response.data.error) {
        throw new Error(response.data?.error || `Failed to ${isEditing ? 'update' : 'create'} knowledge base`);
      }

      return response.data;
    } catch (err: any) {
      reportConsole(`Failed to ${isEditing ? 'update' : 'create'} knowledge base`, 'error', { err });
      throw new Error(err.message || `Failed to ${isEditing ? 'update' : 'create'} knowledge base`);
    }
  }, [getRequestHeaders]);

  const deleteKnowledgeBase = useCallback(async (knowledgeBase: any): Promise<any> => {
    try {
      const response = await fetchWrap({
        url: `${API_BASE_URL}/deleteConfiguration`,
        options: {
          method: 'DELETE',
          body: JSON.stringify({ tenantId: getClientOrg(), knowledgeBase })
        },
        token: gcAccessToken(),
        auth: true,
        cookies: false,
        headerOptions: getRequestHeaders(),
        noContentType: false,
        contentType: 'application/json',
        noResponseData: false
      });

      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to delete knowledge base');
      }

      return response.data;
    } catch (err: any) {
      reportConsole('Failed to delete knowledge base', 'error', { err });
      throw new Error(err.message || 'Failed to delete knowledge base');
    }
  }, [getRequestHeaders]);

  const triggerKnowledgeBaseScan = useCallback(async (knowledgeBase: any): Promise<any> => {
    try {
      const response = await fetchWrap({
        url: `${API_BASE_URL}/runAdHocScan`,
        options: {
          method: 'POST',
          body: JSON.stringify({ tenantId: getClientOrg(), knowledgeBase })
        },
        token: gcAccessToken(),
        auth: true,
        cookies: false,
        headerOptions: getRequestHeaders(),
        noContentType: false,
        contentType: 'application/json',
        noResponseData: false
      });

      if (response.data.message !== 'Workflow triggered successfully') {
        throw new Error(response.data.error || 'Knowledge base scan failed');
      }

      return response.data;
    } catch (err: any) {
      reportConsole('Failed to start knowledge base scan', 'error', { err });
      throw new Error(err.message || 'Failed to start knowledge base scan');
    }
  }, [getRequestHeaders]);

  const getClientTrialUsageDetails = useCallback(async (): Promise<any> => {
    try {
        const trial = await checkUsageTrialForTenant();
        return trial?.data?.results;
          
        } catch (err: any) {
          reportConsole('Failed to fetch client trail usage details', 'error', { err });
          throw new Error(err.message || 'Failed to fetch client trail usage details');
        }
    },[]);
  
  const getKnowledgeBaseCount = useCallback(async (): Promise<any> => {
    try {
      const env = gcEnvironment();
      const allData = [];
      let params: any = {
        "pageSize": 100
      };

      let query = Object.keys(params)
        .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
        .join('&');

       const response = await fetchWrap({
        url: `https://api.${env}/api/v2/knowledge/knowledgebases?${query}`,
        options: {
          method: 'GET',
         
        },
        token: gcAccessToken(),
        auth: true,
        cookies: false,
        headerOptions: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${gcAccessToken()}`
        },
        noContentType: false,
        contentType: 'application/json',
        noResponseData: false
      });
      
      allData.push(...response.data.entities); 
      console.log("alll datas", allData, allData.length)       
      return allData?.length;
    } catch (err : any) {
      reportConsole('Failed to fetch client trail usage details', 'error', { err });
          throw new Error(err.message || 'Failed to fetch client trail usage details');
    }
  }, [])

  return {
    fetchKnowledgeBases,
    fetchConnections,
    fetchSharePointContent,
    createOrUpdateKnowledgeBase,
    deleteKnowledgeBase,
    triggerKnowledgeBaseScan,
    getClientTrialUsageDetails,
    getKnowledgeBaseCount
  };
};